import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import get from 'lodash.get';

import ArticleDek from 'components/Article/ArticleDek';
import ArticleHeroHeadline from 'components/Article/ArticleHero/ArticleHeroHeadline';
import Caption from 'components/Caption';
import InlineVideo from 'components/InlineVideo';
import Picture from 'components/Picture';
import PlaymakerPlayer from 'components/PlaymakerPlayer';
import { NEWS_NOW_DEFAULT_SLATE } from 'components/packages/Playmaker/utils/constants';
import Sponsored from 'components/Sponsored';
import Unibrow from 'components/Unibrow';
import VideoSlate from 'components/VideoPlayer/VideoSlate';
import VideoAutoplayCapabilitiesDetector, { NoDetect } from 'components/VideoPlayer/AutoplayCapabilitiesDetector';

import AIMS_FLAVORS from 'lib/aimsFlavors';
import {
  video as videoPropType,
  socialMediaProfiles as socialPropType,
} from 'lib/CustomPropTypes';
import { AUTOPLAY_ARTICLE_HERO_VIDEOS } from 'lib/brandFeatures.json';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { getPIDforPlayerKey } from 'lib/playmaker';
import { imageAltForImage } from 'lib/imageAlt';
import { sectionContainSlug } from 'lib/taxonomy';

const block = 'article-hero';

const mapStateToProps = ({ shared }) => ({
  socialMediaProfiles: shared.socialMediaProfiles,
});

class ArticleHero extends Component {
  static propTypes = {
    // this needs a new custom proptype called primaryMedia that can accept image/vid or null
    content: videoPropType.isRequired,
    headlineClassName: PropTypes.arrayOf(PropTypes.string),
    isLiveBlog: PropTypes.bool,
    path: PropTypes.string,
    socialMediaProfiles: socialPropType.isRequired,
    stylizedHeader: PropTypes.node,
    stylizedTag: PropTypes.node,
    vertical: PropTypes.string.isRequired,
    isChromeless: PropTypes.bool,
  };

  static childContextTypes = {
    setStickyVideoFlag: PropTypes.func,
  };

  static defaultProps = {
    headlineClassName: [],
    isLiveBlog: false,
    path: '',
    stylizedHeader: null,
    stylizedTag: null,
    isChromeless: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      hasStickyVideo: false,
      isVideoPlaying: false,
    };
  }

  setStickyVideoFlag = (hasStickyVideo) => {
    this.setState({ hasStickyVideo });
  }

  getChildContext = () => ({
    setStickyVideoFlag: this.setStickyVideoFlag,
  });

  getMediaPresentationStyle() {
    const style = get(this.props, 'content.primaryMedia.presentation.style');
    const type = get(this.props, 'content.primaryMedia.type');

    if (type && ['embeddedPlaymakerLiveVideo', 'embeddedVideo'].includes(type)) {
      return 'default';
    }
    if (style && ['default', 'spread'].includes(style)) {
      return style;
    }
    return 'none';
  }

  getMedia = () => {
    const type = get(this.props, 'content.primaryMedia.type');

    switch (type) {
      case 'embeddedVideo':
        return this.renderVideo();
      case 'embeddedPlaymakerLiveVideo':
        return this.renderLiveVideo();
      case 'embeddedImage':
        return this.renderImage();
      default:
        return null;
    }
  }

  renderFeaturedAuthor = () => {
    // featured author is a property on the first author of the authors array
    const { content: { authors } } = this.props;
    if (!Array.isArray(authors) || authors.length === 0) {
      return null;
    }

    const { featuredAuthor: { isFeaturedAuthor }, person: { name } } = authors[0];
    if (!isFeaturedAuthor) {
      return null;
    }

    return (
      <span className="article-hero__featured">
        { name }
        &nbsp;
      </span>
    );
  }

  renderDek = () => {
    const { content: { dek, taxonomy: { sections } } } = this.props;

    if (!dek) {
      return null;
    }

    const isShoppingSection = sectionContainSlug(sections, 'shopping');
    const mediaPresentationStyle = this.getMediaPresentationStyle();

    return (
      <ArticleDek
        dek={dek}
        isShoppingSection={isShoppingSection}
        hasNoImage={mediaPresentationStyle === 'none'}
      />
    );
  }

  renderImage = () => {
    const { content: { primaryMedia } } = this.props;
    const { image: { width, height } = {} } = primaryMedia;
    const url = primaryMedia.image.url.primary;

    return (
      <figure className="article-hero__main">
        <Picture
          url={url}
          className="article-hero__main-image"
          alt={imageAltForImage(primaryMedia.image)}
          flavor="fit"
          responsiveFlavors={{
            s: AIMS_FLAVORS.FIT_760w,
            m: AIMS_FLAVORS.FIT_1000w,
            l: AIMS_FLAVORS.FIT_1240w,
            xl: AIMS_FLAVORS.FIT_2000w,
          }}
          originalHeight={height}
          originalWidth={width}
        />
      </figure>
    );
  }

  renderVideo = () => {
    const {
      vertical, content: { primaryMedia }, socialMediaProfiles, isChromeless,
    } = this.props;
    const autoPlayConfig = getFeatureConfigForBrand(AUTOPLAY_ARTICLE_HERO_VIDEOS, vertical);
    const {
      muted = false,
      chromelessAutoplay = false,
    } = autoPlayConfig || {};
    let {
      autoplay = false,
    } = autoPlayConfig || {};

    autoplay = isChromeless ? chromelessAutoplay : autoplay;

    const VideoPlayerWrapper = autoplay
      ? VideoAutoplayCapabilitiesDetector
      : NoDetect;

    return (
      <div className="article-hero__video">
        <VideoPlayerWrapper>
          {({ ...autoplayCapabilities }) => (
            <InlineVideo
              autoPlay={autoplay}
              autoplayCapabilities={autoplayCapabilities}
              isHero
              onlyAutoplayMuted={muted}
              social={socialMediaProfiles}
              video={primaryMedia.video}
            />
          )}
        </VideoPlayerWrapper>
      </div>
    );
  }

  renderLiveVideo = () => {
    const { isVideoPlaying } = this.state;
    const { vertical } = this.props;
    const playmakerKey = get(this.props, 'content.primaryMedia.playmakerLiveVideo.playmakerId');
    const pid = getPIDforPlayerKey(playmakerKey);

    if (pid) {
      const primaryImage = {
        url: {
          primary: get(
            this.props,
            'content.primaryMedia.playmakerLiveVideo.playmakerTeaseImage.url.primary',
            NEWS_NOW_DEFAULT_SLATE,
          ),
        },
      };

      return (
        <div className="article-hero__video">
          <div className="article-hero__video-outer">
            <div className="article-hero__video-inner">
              {isVideoPlaying ? (
                <PlaymakerPlayer
                  vertical={vertical}
                  autoplayMuted={false}
                  isLive
                  pid={pid}
                  stickyEnabled
                />
              ) : (
                <VideoSlate
                  onPlayClick={() => {
                    this.setState({ isVideoPlaying: true });
                  }}
                  pageView="article"
                  showTeaseImage
                  vertical={vertical}
                  video={{
                    primaryImage,
                  }}
                />
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  getSponsor = () => {
    const { content: { nativeAd, source } } = this.props;
    if (!nativeAd || !source) {
      return null;
    }
    const { organization: { externalUrl, primaryImage } = {} } = source;
    return (
      <div className="article-hero__sponsor">
        <Sponsored
          externalUrl={externalUrl}
          primaryImage={primaryImage}
        />
      </div>
    );
  }

  render() {
    const {
      content: {
        ecommerceEnabled,
        headline,
        presentation,
        primaryMedia,
        taxonomy: { sections },
        unibrow,
      },
      headlineClassName,
      isLiveBlog,
      stylizedHeader,
      stylizedTag,
      path,
      vertical,
    } = this.props;

    if (presentation && presentation.style && presentation.style === 'shell') {
      return null;
    }

    const mediaPresentationStyle = this.getMediaPresentationStyle();
    const { hasStickyVideo } = this.state;
    const isShoppingSection = sectionContainSlug(sections, 'shopping');

    const media = this.getMedia();
    const sponsor = this.getSponsor();
    const hasCaption = primaryMedia && primaryMedia.type !== 'embeddedVideo' && primaryMedia.image;

    const containerClasses = classNames(
      'article-hero__container',
      [mediaPresentationStyle],
      {
        'article-hero__shopping-section-container': isShoppingSection,
        'article-hero__live-blog': isLiveBlog,
        'article-hero__sticky-video': hasStickyVideo,
      },
    );

    return (
      <section className={containerClasses} data-test={block}>
        <div className="article-hero__bg-container">
          <header
            className={classNames(
              'article-hero__header',
              'mh0-print',
              'layout-grid-container',
              {
                'article-hero__ecommerce-enabled': ecommerceEnabled || isShoppingSection,
                'article-hero__is-tagged': !!stylizedTag,
                'article-hero__shopping-section': isShoppingSection,
              },
            )}
            data-test="article-hero__header"
          >
            {stylizedTag}
            <aside className="article-hero__unibrow-grid layout-grid-item grid-col-2-xl">
              <Unibrow
                size="div"
                className={classNames(
                  'article-hero__tax-term',
                  {
                    'article-hero__ecommerce-enabled': ecommerceEnabled,
                    'article-hero__shopping-section': isShoppingSection,
                  },
                )}
                unibrow={unibrow}
              />
            </aside>
            <ArticleHeroHeadline
              className={classNames(
                'article-hero__headline',
                headlineClassName,
                { 'article-hero__shopping-section': isShoppingSection },
              )}
              dek={this.renderDek()}
              isLiveBlog={isLiveBlog}
              isShoppingSection={isShoppingSection}
              path={path}
              vertical={vertical}
            >
              {stylizedHeader}
              {this.renderFeaturedAuthor()}
              {headline.primary}
            </ArticleHeroHeadline>
          </header>
        </div>
        {(media || hasCaption || sponsor) && (
          <div className="article-hero__media-holder layout-grid-container mb8">
            {media && (
              <div
                className={classNames(
                  'article-hero__media-container layout-grid-item',
                  {
                    'grid-col-10-xl grid-col-push-2-xl': !isLiveBlog,
                    'grid-col-7_5-l grid-col-6-xl grid-col-push-2-xl': isLiveBlog,
                  },
                )}
              >
                {media}
              </div>
            )}
            {hasCaption && (
              <Caption
                image={primaryMedia.image}
                className={classNames(
                  'article-hero__caption layout-grid-item',
                  {
                    'grid-col-10-xl grid-col-push-2-xl': !isLiveBlog,
                    'grid-col-6-xl grid-col-push-2-xl': isLiveBlog,
                  },
                )}
              />
            )}
            {sponsor}
          </div>
        )}
      </section>
    );
  }
}

export default connect(mapStateToProps)(ArticleHero);
